import React, { useState } from 'react';
import { Sparkles, X, Gift, ArrowRight, Tag, Info, Megaphone } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import { MarketingCard } from '../../types';

import { saveGeneratedCoupon } from '../../services/dbService';

export const MarketingFloatingWidget: React.FC = () => {
    const { settings, setCart, setView, products, cart, selectedProduct, setSelectedProduct, showToast, view, t, resolveLang, isKioskMode } = useApp();

    const handleAddToCart = (e: React.MouseEvent, productId: string) => {
        e.stopPropagation();
        const product = products?.find(p => p.id === productId);
        if (!product) return;
        
        setSelectedProduct(product);
        setIsOpen(false);
        setSelectedCard(null);
        if (view === 'intro') {
            setView('intro');
        } else {
            setView('builder');
        }
    };
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<MarketingCard | null>(null);
    const [rotationCount, setRotationCount] = useState(0);

    React.useEffect(() => {
        // Avvia il primo giro dopo mezzo secondo
        const to = setTimeout(() => setRotationCount(1), 500);
        // Ripete il giro ogni 5 minuti (300000 ms)
        const interval = setInterval(() => {
            setRotationCount(c => c + 1);
        }, 300000);
        return () => { clearTimeout(to); clearInterval(interval); };
    }, []);

    React.useEffect(() => {
        if (isOpen || selectedCard) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, selectedCard]);

    if (!settings.isMarketingCardsEnabled) return null;
    
    // Visibility conditions
    const allowedViews = ['intro', 'menu-type', 'menu', 'products', 'categories', 'builder'];
    if (!allowedViews.includes(view)) return null;
    if (selectedProduct) return null; // Hide during product customization
    
    // Filter active cards
    const activeCards = settings.marketingCards?.filter(c => c.isActive) || [];
    if (activeCards.length === 0) return null;

    const handleApplyCoupon = async (card: MarketingCard) => {
        if (!card.couponCode) return;
        
        // Generate a 4-character random alphanumeric suffix
        const generateSuffix = () => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let res = '';
            for (let i = 0; i < 4; i++) res += chars.charAt(Math.floor(Math.random() * chars.length));
            return res;
        };
        const uniqueCouponCode = `${card.couponCode}-${generateSuffix()}`;

        try {
            navigator.clipboard.writeText(uniqueCouponCode);
        } catch(e) {}
        
        const expiresAt = Date.now() + 60 * 60 * 1000; // 1 hour expiration
        
        // Store in Firebase generated coupons list
        await saveGeneratedCoupon(uniqueCouponCode, expiresAt, card);
        
        localStorage.setItem('pendingCoupon', uniqueCouponCode);
        
        const discountStr = (t('marketing_discount_applied') as string).replace('{code}', uniqueCouponCode);
        showToast(discountStr);
        
        // Se ci sono prodotti collegati, offri di aggiungerli (semplificato andando al menu)
        setSelectedCard(null);
        setIsOpen(false);
        if (card.linkedProductIds && card.linkedProductIds.length > 0 && products) {
            const prod = products.find(p => p.id === card.linkedProductIds![0]);
            if (prod) {
                setSelectedProduct(prod);
            }
        }
        if (view === 'intro') {
            setView('intro');
        } else {
            setView('builder');
        }
    };

    const handleOrderNow = () => {
        if (selectedCard?.linkedProductIds && selectedCard.linkedProductIds.length > 0 && products) {
            const prod = products.find(p => p.id === selectedCard.linkedProductIds![0]);
            if (prod) {
                setSelectedProduct(prod);
            }
        }
        setSelectedCard(null);
        setIsOpen(false);
        if (view === 'intro') {
            setView('intro');
        } else {
            setView('builder');
        }
    };

    return (
        <>
            {/* Floating Button */}
            {!isOpen && (
                <motion.button 
                    drag={!isKioskMode}
                    dragMomentum={false}
                    onClick={() => setIsOpen(true)}
                    className={`fixed z-[100] flex items-center group ${isKioskMode ? 'top-6 right-24' : 'bottom-24 right-4 sm:bottom-8 sm:right-8 gap-3'}`}
                    animate={isKioskMode ? { y: [0, -8, 0] } : undefined}
                    transition={isKioskMode ? { duration: 2.5, repeat: Infinity, ease: "easeInOut" } : undefined}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    {isKioskMode ? (
                        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600 text-white rounded-full shadow-[0_15px_40px_rgba(236,72,153,0.5)] px-8 py-5 flex items-center gap-4 border-4 border-white backdrop-blur-md relative overflow-hidden group-hover:shadow-[0_20px_50px_rgba(236,72,153,0.7)] transition-all">
                            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                            <Gift size={40} className="relative z-10 animate-bounce drop-shadow-md text-white" />
                            <div className="flex flex-col items-start relative z-10">
                                <span className="font-black text-2xl leading-none tracking-widest uppercase drop-shadow-md text-white">{t('marketing_btn')}</span>
                                <span className="text-base font-bold opacity-100 drop-shadow-md text-white/90 uppercase tracking-widest mt-1">Tocca per scoprire</span>
                            </div>
                            <Sparkles size={28} className="text-yellow-300 animate-pulse ml-3 relative z-10 drop-shadow-lg" />
                        </div>
                    ) : (
                        <>
                            <div className="relative bg-gradient-to-r from-pink-500 to-indigo-600 text-white p-3 sm:p-4 rounded-full shadow-2xl ring-4 ring-pink-500/20 z-10 flex items-center justify-center">
                                <Gift size={28} className="group-hover:-rotate-12 transition-transform duration-300 relative z-20" />
                                {/* Sparkles icon for desktop (since circular text is hidden on desktop) */}
                                <Sparkles size={14} className="hidden sm:block absolute -top-2 -right-2 text-yellow-300 animate-pulse z-20" />
                                
                                {/* Circular text wrapper for mobile */}
                                <div className="absolute inset-0 flex items-center justify-center sm:hidden pointer-events-none z-0">
                                    <motion.div 
                                        className="w-[120px] h-[120px] absolute flex items-center justify-center" 
                                        initial={{ rotate: 35 }}
                                        animate={{ rotate: 35 + (rotationCount * 360) }}
                                        transition={{ duration: 2.2, ease: "easeInOut" }}
                                    >
                                        <svg className="w-full h-full absolute" viewBox="0 0 120 120" overflow="visible">
                                            <defs>
                                                <path id="circlePathTop" d="M 26,60 A 34,34 0 0,1 94,60" />
                                                <path id="circlePathBottom" d="M 14,60 A 46,46 0 0,0 106,60" />
                                            </defs>
                                            <text fontSize="14" fill="currentColor" fontWeight="900" className="text-pink-600 dark:text-pink-400 uppercase drop-shadow-md" style={{ stroke: 'rgba(255,255,255,0.9)', strokeWidth: '2.5px', paintOrder: 'stroke fill', letterSpacing: '0.15em' }}>
                                                <textPath href="#circlePathTop" startOffset="50%" textAnchor="middle">
                                                    {t('marketing_offers')}
                                                </textPath>
                                                <textPath href="#circlePathBottom" startOffset="50%" textAnchor="middle">
                                                    {t('marketing_news')}
                                                </textPath>
                                            </text>
                                        </svg>
                                        <Sparkles size={14} className="absolute right-[10px] text-yellow-300 animate-pulse z-20" />
                                    </motion.div>
                                </div>
                            </div>
                            
                            <span className="font-black hidden sm:inline-block tracking-wide pr-3 py-3 px-6 bg-white dark:bg-gray-800 rounded-full shadow-xl shadow-pink-500/10 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-indigo-600 ml-1 border border-pink-100 dark:border-pink-900/30">
                                {t('marketing_btn')}
                            </span>
                        </>
                    )}
                </motion.button>
            )}

            {/* List Modal */}
            {isOpen && !selectedCard && (
                <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex justify-center items-end sm:items-center p-0 sm:p-4 animate-in fade-in" onClick={() => setIsOpen(false)}>
                    <div 
                        className="bg-gray-100 dark:bg-gray-900 w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden animate-in slide-in-from-bottom"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="p-4 sm:p-6 bg-white dark:bg-gray-800 flex justify-between items-center shadow-sm z-10 relative shrink-0">
                            <h2 className="text-xl font-black text-gray-800 dark:text-white flex items-center gap-2">
                                <Megaphone className="text-pink-500" size={24} /> 
                                {t('whats_new')}
                            </h2>
                            <button onClick={() => setIsOpen(false)} className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-500 hover:text-gray-800 dark:text-gray-300"><X size={20}/></button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 pt-4">
                            {activeCards.map(card => (
                                <div 
                                    key={card.id} 
                                    onClick={() => setSelectedCard(card)}
                                    className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-md cursor-pointer hover:shadow-xl transition-shadow group border border-gray-100 dark:border-gray-700"
                                >
                                    {card.imageUrl && (
                                        <div className="w-full h-48 sm:h-56 relative overflow-hidden">
                                            <img src={card.imageUrl} alt={resolveLang(card, 'title')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            {card.type === 'offer' && (
                                                <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                                                    <Tag size={12}/> {t('marketing_offer')}
                                                </div>
                                            )}
                                            {card.type === 'news' && (
                                                <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                                                    <Info size={12}/> {t('marketing_news')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="p-4">
                                        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1 group-hover:text-pink-500 transition-colors">{resolveLang(card, 'title')}</h3>
                                        <p className="text-sm text-gray-500 line-clamp-2">{resolveLang(card, 'shortDescription')}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {selectedCard && (
                <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex justify-center items-end sm:items-center p-0 sm:p-4 animate-in fade-in" onClick={() => setSelectedCard(null)}>
                    <div 
                        className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-in slide-in-from-bottom"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="p-4 border-b border-gray-100 dark:border-gray-800 shrink-0 flex justify-between items-center">
                            {selectedCard.type === 'offer' ? (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs font-bold uppercase tracking-wider">
                                    <Tag size={12}/> {t('marketing_special_offer')}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-bold uppercase tracking-wider">
                                    <Info size={12}/> {t('marketing_news')}
                                </span>
                            )}
                            <button onClick={() => setSelectedCard(null)} className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-500 hover:text-gray-800 dark:text-gray-300"><X size={20}/></button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto w-full">
                            {selectedCard.imageUrl && (
                                <div className="w-full relative aspect-square sm:aspect-video overflow-hidden">
                                    <img src={selectedCard.imageUrl} alt={resolveLang(selectedCard, 'title')} className="w-full h-full object-cover" />
                                </div>
                            )}
                            
                            <div className="p-6">
                                <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-2">{resolveLang(selectedCard, 'title')}</h2>
                                <p className="text-lg text-gray-500 dark:text-gray-300 font-medium mb-6">{resolveLang(selectedCard, 'shortDescription')}</p>
                                
                                <div className="prose prose-sm dark:prose-invert">
                                    <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-400 leading-relaxed font-medium">
                                        {resolveLang(selectedCard, 'detailedDescription')}
                                    </p>
                                </div>

                                {selectedCard.linkedProductIds && selectedCard.linkedProductIds.length > 0 && products && (
                                    <div className="mt-8 space-y-3">
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 border-b border-gray-100 dark:border-gray-800 pb-2">{t('marketing_featured_products')}</h3>
                                        {selectedCard.linkedProductIds.map(pid => {
                                            const prod = products.find(p => p.id === pid);
                                            if (!prod) return null;
                                            return (
                                                <div key={prod.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm gap-3">
                                                    <div className="flex items-center gap-3 w-full sm:w-auto">
                                                        {prod.imageUrl ? (
                                                            <img src={prod.imageUrl} alt={resolveLang(prod, 'name')} className="w-12 h-12 rounded-lg object-cover" />
                                                        ) : (
                                                            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                                                <Tag size={20} className="text-gray-400" />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">{resolveLang(prod, 'name')}</h4>
                                                            <p className="text-pink-500 font-bold text-sm">€{prod.basePrice.toFixed(2)}</p>
                                                        </div>
                                                    </div>
                                                    <button 
                                                        onClick={(e) => handleAddToCart(e, prod.id)}
                                                        className="w-full sm:w-auto px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg font-bold text-xs hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
                                                    >
                                                        {t('marketing_add_cart')}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 shrink-0">
                            {selectedCard.type === 'offer' ? (
                                <button 
                                    onClick={() => handleApplyCoupon(selectedCard)}
                                    className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-black rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_20px_rgba(236,72,153,0.3)] flex items-center justify-center gap-2"
                                >
                                    <Gift size={22} />
                                    {t('marketing_get_discount')}
                                </button>
                            ) : (
                                <button 
                                    onClick={handleOrderNow}
                                    className="w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-black rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-xl flex items-center justify-center gap-2"
                                >
                                    {t('marketing_order_now')} <ArrowRight size={22} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

